import {
  CALCULATOR_GROUP_SOIL_AMENDMENT,
  CALCULATOR_LIME_MATE,
  CALCULATOR_SODI_CALC,
  LIME_MATE_NAME,
  LIME_MATE_PICKER_DESCRIPTION,
  LIME_MATE_SUBTITLE,
  LIME_MATE_TITLE,
  SODI_CALC_NAME,
  SODI_CALC_PICKER_DESCRIPTION,
  SODI_CALC_SUBTITLE,
  SODI_CALC_TITLE,
} from 'constants/uiConstants';
import {
  CALCULATORS_PAGE_PATH,
  FARMS_URL_PARAM_NODE,
} from 'constants/routeConstants';

/**
 * The calculators (OP-2585) — what each is called and where it's grouped.
 * The components live in `components/Calculators/calculatorComponents`; this
 * module stays free of React so Redux can validate a saved id against it.
 */
export type CalculatorId =
  | typeof CALCULATOR_LIME_MATE
  | typeof CALCULATOR_SODI_CALC;

/**
 * Starting values a host can pass in — e.g. from the Farms tree selection.
 * Each calculator only uses what it has a field for.
 */
export interface CalculatorAutofill {
  /** "Client, Farm, Paddock", shown and edited in the report. */
  siteLabel?: string;
  /** Site area in ha. */
  areaHa?: number | null;
}

export interface CalculatorDefinition {
  id: CalculatorId;
  /** Short name — the picker and the RH panel. */
  name: string;
  /** Page heading. */
  title: string;
  subtitle: string;
  /** One line under the name in the picker. */
  description: string;
  /** Picker group, so a long list stays scannable. */
  group: string;
  /**
   * Lays inputs and results side by side when there's room, so the full page
   * uses a wider column for it. Bulk mode gets the widest column.
   */
  wide: boolean;
}

/** In picker and landing order; the first is the RH panel's default. */
export const CALCULATORS: CalculatorDefinition[] = [
  {
    id: CALCULATOR_LIME_MATE,
    name: LIME_MATE_NAME,
    title: LIME_MATE_TITLE,
    subtitle: LIME_MATE_SUBTITLE,
    description: LIME_MATE_PICKER_DESCRIPTION,
    group: CALCULATOR_GROUP_SOIL_AMENDMENT,
    wide: false,
  },
  {
    id: CALCULATOR_SODI_CALC,
    name: SODI_CALC_NAME,
    title: SODI_CALC_TITLE,
    subtitle: SODI_CALC_SUBTITLE,
    description: SODI_CALC_PICKER_DESCRIPTION,
    group: CALCULATOR_GROUP_SOIL_AMENDMENT,
    wide: true,
  },
];

export const DEFAULT_CALCULATOR_ID: CalculatorId = CALCULATORS[0].id;

export const isCalculatorId = (value: unknown): value is CalculatorId =>
  CALCULATORS.some((calculator) => calculator.id === value);

export const getCalculator = (id: CalculatorId): CalculatorDefinition =>
  CALCULATORS.find((calculator) => calculator.id === id) ?? CALCULATORS[0];

const withNode = (path: string, siteNodeKey?: string | null): string =>
  siteNodeKey
    ? `${path}?${new URLSearchParams({ [FARMS_URL_PARAM_NODE]: siteNodeKey }).toString()}`
    : path;

/**
 * A calculator's full-page URL. An optional farm/paddock node key travels in
 * the same `node` param /farms uses, so the page can autofill the site.
 */
export const buildCalculatorHref = (
  id: CalculatorId,
  siteNodeKey?: string | null
): string => withNode(`${CALCULATORS_PAGE_PATH}/${id}`, siteNodeKey);

/** The Calculators landing page, keeping the node key for the next pick. */
export const buildCalculatorsHomeHref = (siteNodeKey?: string | null) =>
  withNode(CALCULATORS_PAGE_PATH, siteNodeKey);

/** Calculators by group, in list order — the landing page's sections. */
export const groupCalculators = (): {
  group: string;
  calculators: CalculatorDefinition[];
}[] => {
  const groups = new Map<string, CalculatorDefinition[]>();
  CALCULATORS.forEach((calculator) => {
    groups.set(calculator.group, [
      ...(groups.get(calculator.group) ?? []),
      calculator,
    ]);
  });
  return Array.from(groups, ([group, calculators]) => ({
    group,
    calculators,
  }));
};
